import DisciplinesService from "@/services/DisciplinesService";
import { validateRestrictionData } from "@/utils/helpers";

export default {
    state() {
        return {
            discipline: {
                id: "",
                name: "",
                companyId: "",
                visible: true,
                topics: [],
                tags: [],
                materials: [],
                inscriptions: [],
                isEditable: false,
                canGenerateTopics: false,
                owner: []
            },
            disciplinesAmount: 0,
            disciplinesData: "",
            duplicatedDisciplineData: "",
            disciplineAnalysisData: "",
            disciplinesFeedback: [],
            disciplineFeedback: {},
            disciplineComments: {},
            renderDisciplinesFeedback: false,
            lastRouteBeforeDisciplineFeedbacks: null
        }
    },

    getters: {
        getLastRouteBeforeDisciplineFeedbacks: (state) => state.lastRouteBeforeDisciplineFeedbacks,
        getRenderDisciplinesFeedback: (state) => state.renderDisciplinesFeedback,
        getDisciplineComments: (state) => state?.disciplineComments,
        getDisciplineFeedback: (state) => state?.disciplineFeedback,
        getDisciplinesFeedback: (state) => state?.disciplinesFeedback,
        getDisciplineId: (state) => state?.discipline?.id,
        getDisciplinesAmount: (state) => state.disciplinesAmount,
        getDisciplineName: (state) => state.discipline.name,
        getDisciplineCompanyId: (state) => state.discipline.companyId,
        getDisciplinesData: (state) => state.disciplinesData,
        getDisciplineData: (state) => state.discipline,
        getDisciplineConsumersInUse: (state) => state?.discipline?.consumers_in_use,
    },

    mutations: {
        setLastRouteBeforeDisciplineFeedbacks(state, data) {
            state.lastRouteBeforeDisciplineFeedbacks = data || null
        },

        setRenderDisciplinesFeedback(state, data) {
            state.renderDisciplinesFeedback = data || false
        },

        setDisciplineComments(state, data) {
            state.disciplineComments = data || {}
        },

        setDisciplineFeedback(state, data) {
            state.disciplineFeedback = data || {}
        },

        setDisciplinesFeedback(state, data) {
            state.disciplinesFeedback = data || []
        },

        updateDisciplineProgress(state, data) {
            state.discipline.progress = data
        },

        insertNewTags(state, payload) {
            const disciplineIndex = state.disciplinesData.findIndex((disciplineInArray) => disciplineInArray.id === payload.disciplineId)
            payload.disciplineTagsData.forEach((tagInArray) => {
                state.disciplinesData[disciplineIndex].tags.push(tagInArray)
            })
        },

        setDisciplineData(state, data) {
            data && (state.discipline = data)
        },
        resetDiscipline(state) {
            state.discipline = {
                name: "",
                companyId: null,
            };
        },

        resetDisciplineData(state) {
            state.disciplinesData = []
        },

        manipulateDisciplinesAmount(state, data) {
            data === 'increment' ? state.disciplinesAmount++ : state.disciplinesAmount--
        },

        setDisciplinesAmount(state, data) {
            state.disciplinesAmount = data || 0
        },

        setDisciplinesData(state, data) {
            state.disciplinesData = data
        },

        setDuplicatedDisciplineData(state, data) {
            state.duplicatedDisciplineData = data
        },

        setDisciplineAnalysisData(state, data) {
            state.disciplineAnalysisData = data
        },

        setNewDiscipline(state, data) {
            data && (state.disciplinesData.push(data))
        },

        orderDisciplineTopics(state, data) {
            const disciplineIndex = state.disciplinesData.findIndex((d) => d.id === data.disciplineId)
            if (disciplineIndex !== -1) {
                state.disciplinesData[disciplineIndex].topics = data.topicsOrdered
            }
        },

        updateFavoriteDisciplineState(state, data) {
            if (data && Array.isArray(state.disciplinesData)) {
                const disciplineIndex = state.disciplinesData.findIndex((discipline) => discipline?.id === data?.id)
                if (disciplineIndex !== -1) {
                    if (!data.is_favorite && data.favoriteDisciplinesFilter) {
                        state.disciplinesData.splice(disciplineIndex, 1)
                        return
                    }

                    state.disciplinesData[disciplineIndex].is_favorite = data.is_favorite
                }
            }
        },

        removeDisciplineRestrictionToConsumers(state, data) {
            const { validPayload, contentManipulatedId, consumersId } = validateRestrictionData(data)
            if (!validPayload) return

            const disciplineIndex = state.disciplinesData.findIndex((discipline) => discipline?.id === contentManipulatedId)
            if (disciplineIndex !== -1) {
                const discipline = state.disciplinesData[disciplineIndex]

                discipline.restricted_consumers = discipline.restricted_consumers.filter((consumerId) => !consumersId.includes(consumerId))
            }
        },

        restrictDisciplineToConsumers(state, data) {
            const { validPayload, contentManipulatedId, consumersId } = validateRestrictionData(data)
            if (!validPayload) return

            const disciplineIndex = state.disciplinesData.findIndex((discipline) => discipline?.id === contentManipulatedId)
            if (disciplineIndex !== -1) {
                const discipline = state.disciplinesData[disciplineIndex]
                !discipline.restricted_consumers && (discipline.restricted_consumers = [])

                const newConsumers = consumersId.filter(id => !discipline.restricted_consumers.includes(id))
                discipline.restricted_consumers = [
                    ...discipline.restricted_consumers,
                    ...newConsumers
                ]
            }
        },
    },

    actions: {
        setLastRouteBeforeDisciplineFeedbacks({ commit }, payload) {
            commit('setLastRouteBeforeDisciplineFeedbacks', payload)
        },

        setDisciplineFeedback({ commit }, payload) {
            commit('setDisciplineFeedback', payload)
        },

        setRenderDisciplinesFeedback({ commit }, payload) {
            commit('setRenderDisciplinesFeedback', payload)
        },

        manipulateDisciplinesAmount({ commit }, payload) {
            commit('manipulateDisciplinesAmount', payload)
        },

        updateDisciplineProgress({ commit }, data) {
            commit('updateDisciplineProgress', data)
        },

        orderDisciplineTopics({ commit }, payload) {
            commit('orderDisciplineTopics', payload)
        },

        resetDisciplineData({ commit }) {
            commit('resetDisciplineData')
        },

        async getDisciplineComments({ commit, dispatch }, payload) {
            try {
                const response = await DisciplinesService.getDisciplineComments(payload)
                commit('setDisciplineComments', response)
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
                throw error
            } finally {
                payload?.skeletonKey && (dispatch('skeleton/setLoadingState', { key: payload.skeletonKey, value: false }, { root: true }))
            }
        },

        async getDisciplineFeedback({ commit, dispatch }, payload) {
            try {
                const response = await DisciplinesService.getDisciplineFeedback(payload?.disciplineId || null)
                commit('setDisciplineFeedback', response)
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
                throw error
            } finally {
                payload?.skeletonKey && (dispatch('skeleton/setLoadingState', { key: payload.skeletonKey, value: false }, { root: true }))
            }
        },

        async getDisciplinesFeedback({ commit, dispatch }, payload) {
            try {
                const response = await DisciplinesService.getDisciplinesFeedback(payload)
                commit('setDisciplinesFeedback', response?.data)
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            } finally {
                payload?.skeletonKey && (dispatch('skeleton/setLoadingState', { key: payload.skeletonKey, value: false }, { root: true }))
            }
        },

        async exportDisciplineFeedbacks({ commit, dispatch }, payload) {
            try {
                dispatch('setLoadingState', { loadignState: true, loadingView: 'Discipline Feedback' })
                await DisciplinesService.exportDisciplineFeedbacks(payload)
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            } finally {
                dispatch('setLoadingState', { loadignState: false })
            }
        },

        async createDiscipline({ commit }, payload) {
            try {
                const response = await DisciplinesService.createDiscipline(payload.discipline, payload.userToken);
                
                const formattedResponse = !!response?.data ? {
                    ...response.data,
                    topics: [],
                    tags: []
                } : null
                
                commit('setNewDiscipline', formattedResponse);
                commit('manipulateDisciplinesAmount', 'increment')
                return formattedResponse;
            } catch (error) {
                console.error('Erro ao adicionar disciplina:', error);

                throw error;
            }
        },

        async findDiscipline({ commit }, payload) {
            try {
                const response = await DisciplinesService.findDiscipline(payload);

                commit('setDisciplineData', response.data);

                return response.data;
            } catch (error) {
                console.error('Erro ao consultar disciplina:', error);

                throw new Error('Não foi possível consultar disciplina. Por favor, tente novamente.');
            }
        },

        async updateDiscipline({ commit }, payload) {
            try {
                const response = await DisciplinesService.updateDiscipline(payload.discipline, payload.userToken);

                commit('setDisciplineData', response.data);

                return response.data;
            } catch (error) {
                console.error('Erro ao atualizar disciplina:', error);

                throw new Error('Não foi possível atualizar a disciplina. Por favor, tente novamente.');
            }
        },

        async deleteDiscipline({ commit }, payload) {
            try {
                const response = await DisciplinesService.deleteDiscipline(payload.disciplineId, payload.userToken);

                commit('resetDiscipline');

                return response.data;
            } catch (error) {
                console.error('Erro ao deletar disciplina:', error);

                throw new Error('Não foi possível deletar a disciplina. Por favor, tente novamente.');
            }
        },

        async getDisciplines({ commit, dispatch }, payload) {
            try {
                const response = await DisciplinesService.getDisciplines(payload.data, payload.userToken);
                commit('setDisciplinesAmount', response?.total)
                commit('setDisciplinesData', response.data);

                return response.data;
            } catch (error) {
                console.error('Erro ao consultar disciplinas:', error);

                throw new Error('Não foi possível consultar as disciplinas. Por favor, tente novamente.');
            } finally {
                if (payload.skeletonKey) {
                    dispatch('skeleton/setLoadingState', { key: payload.skeletonKey, value: false }, { root: true })
                }
            }
        },

        async insertTags({ commit }, payload) {
            try {
                const response = await DisciplinesService.insertTags(payload.data, payload.userToken);
                commit('insertNewTags', payload.data)
                commit('resetDiscipline');

                return response.data;
            } catch (error) {
                console.error('Erro ao inserir tags:', error);

                throw new Error('Não foi possível inserir tags. Por favor, tente novamente.');
            }
        },

        async removeTag({ commit }, payload) {
            try {
                const response = await DisciplinesService.removeTag(payload.data, payload.userToken);

                commit('resetDiscipline');

                return response.data;
            } catch (error) {
                console.error('Erro ao remover tag:', error);

                throw new Error('Não foi possível remover tag. Por favor, tente novamente.');
            }
        },

        async duplicateDiscipline({ commit }, payload) {
            try {
                const response = await DisciplinesService.duplicateDiscipline(payload.disciplineId, payload.userToken);

                commit('setDuplicatedDisciplineData');

                return response.data;
            } catch (error) {
                console.log('Erro ao duplicar disciplina:', error);

                throw new Error('Não foi possível duplicar disciplina. Por favor, tente novamente.');
            }
        },

        async disciplineAnalysis({ commit }, payload) {
            try {
                const response = await DisciplinesService.disciplineAnalysis(payload.data, payload.userToken);

                commit('setDisciplineAnalysisData', response);

                return response.data;
            } catch (error) {
                console.error('Erro ao analisar ementa:', error);

                throw new Error('Não foi possível analisar ementa. Por favor, tente novamente.');
            }
        },

        async updateDisciplineFavoriteState({ commit, dispatch }, payload) {
            try {
                const response = await DisciplinesService.updateDisciplineFavoriteState(payload.disciplineId)
                commit('updateFavoriteDisciplineState', {
                    id: response?.data?.id,
                    is_favorite: response?.data?.is_favorite,
                    favoriteDisciplinesFilter: payload.favoriteStatus
                })

                dispatch('toastfy/activeSuccesNotification', { text: "Status de disciplina alterado com sucesso" })
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            }
        },

        async restrictDisciplineToConsumers({ commit, dispatch }, payload) {
            try {
                await DisciplinesService.restrictDisciplineToConsumers(payload)
                commit('restrictDisciplineToConsumers', payload)
                dispatch('toastfy/activeSuccesNotification', { text: "Restrição aplicada a consumidores com sucesso!" })
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            }
        },

        async removeDisciplineRestrictionToConsumers({ commit, dispatch }, payload) {
            try {
                await DisciplinesService.removeDisciplineRestrictionToConsumers(payload)
                commit('removeDisciplineRestrictionToConsumers', payload)
                dispatch('toastfy/activeSuccesNotification', { text: "Restrição removida a consumidores com sucesso!" })
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            }
        },

        async syncDisciplineGrades({ commit, dispatch }, payload) {
            try {
                const response = await DisciplinesService.syncDisciplineGrades(payload.disciplineId, payload.userToken)
                
                if (response?.status === 200) {
                    dispatch('toastfy/activeSuccesNotification', { text: 'As notas estão sendo sincronizadas. Você receberá uma notificação quando o processo for concluído.' })
                } else if (response?.status === 204) {
                    dispatch('toastfy/activeInfoNotification', { text: 'Não há notas para sincronizar.' })
                }
                
                return response;
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error.message || 'Erro ao sincronizar notas' })
                throw error;
            }
        },
    }
}